import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Scale, Landmark, Compass } from "lucide-react";
import { getArticles, getFormations, getPartenairesPublic } from "@/lib/api";
import { Marquee } from "@/components/Marquee";
import { Temoignages } from "@/components/Temoignages";
import { Reveal, MaskedLine } from "@/components/Reveal";

const HERO_IMG = "https://images.pexels.com/photos/5257003/pexels-photo-5257003.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const etiquettePrix = (f) =>
  f.passee ? "Déjà offerte" : f.prix == null ? "Inscription via Eventbrite" : f.prix === 0 ? "Gratuite" : `${f.prix} $`;

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-espresso text-ivory" data-testid="hero-section">
      <div className="mx-auto grid min-h-[92vh] max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <h1 className="font-serif text-5xl font-medium leading-[1.08] tracking-tight md:text-6xl lg:text-7xl" data-testid="hero-title">
            <MaskedLine delay={0.35}>Le droit, la fiscalité</MaskedLine>
            <MaskedLine delay={0.5}>et la stratégie,</MaskedLine>
            <MaskedLine delay={0.65}>
              <span className="italic text-primary">accessibles</span> à toutes et à tous.
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-ivory/70 md:text-lg"
            data-testid="hero-subtitle"
          >
            La Fondation HORA, anciennement connue sous le nom de la Clinique Juridique
            d'affaires, est fondée en 2021 et démocratise l'accès à l'éducation, à
            l'expertise spécialisée, aux ressources et au soutien financier pour que chacun
            puisse développer son potentiel, prendre des décisions éclairées et bâtir des
            activités durables et porteuses d'impact.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/don"
              data-testid="hero-donate-button"
              className="rounded-sm bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(22,83%,40%)]"
            >
              Faire un don
            </Link>
            <Link
              to="/formations"
              data-testid="hero-formations-button"
              className="rounded-sm border border-ivory/25 px-8 py-4 text-sm font-semibold text-ivory transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 hover:border-gold hover:text-gold"
            >
              Découvrir nos formations
            </Link>
            <Link
              to="/devenir-benevole"
              data-testid="hero-volunteer-link"
              className="group flex items-center gap-2 text-sm font-medium text-ivory/60 transition-colors hover:text-gold"
            >
              Devenir bénévole
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>

        <div className="relative hidden lg:col-span-5 lg:block">
          <div className="absolute -left-6 -top-6 h-full w-full border border-gold/40" aria-hidden="true" />
          <motion.div style={{ y: imgY }} className="relative overflow-hidden" data-testid="hero-image-frame">
            <motion.img
              initial={{ scale: 1.15, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.4, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              src={HERO_IMG}
              alt="Entrepreneures et entrepreneurs collaborant autour d'une table"
              className="aspect-[4/5] w-full object-cover"
              data-testid="hero-image"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Stats = () => (
  <section className="border-b border-espresso-border bg-espresso pb-16 text-ivory" data-testid="stats-section">
    <div className="mx-auto grid max-w-7xl gap-10 border-t border-espresso-border px-6 pt-14 md:grid-cols-3 lg:px-10">
      {[
        { chiffre: "6", libelle: "consultations téléphoniques gratuites" },
        { chiffre: "641", libelle: "personnes ont assisté à nos formations" },
        { chiffre: "27", libelle: "événements organisés depuis 2021" },
      ].map((s, i) => (
        <Reveal key={s.libelle} delay={i * 0.12}>
          <p className="font-serif text-5xl font-medium text-gold md:text-6xl" data-testid={`stat-chiffre-${i}`}>{s.chiffre}</p>
          <p className="mt-3 text-sm text-ivory/60">{s.libelle}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Manifesto = () => (
  <section className="bg-background py-28 md:py-36" data-testid="manifesto-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Notre manifeste</p>
      </Reveal>
      <div className="mt-16 space-y-20">
        {[
          {
            num: "01",
            titre: "Notre mission",
            texte: "Favoriser l'autonomie économique et la résilience des personnes et des organisations en démocratisant l'accès à l'éducation, à l'expertise spécialisée, aux ressources et au soutien financier nécessaires pour bâtir des activités durables et porteuses d'impact.",
          },
          {
            num: "02",
            titre: "Notre approche",
            texte: "Éduquer, donner accès à l'expertise, soutenir et financer : quatre leviers complémentaires pour développer les capacités, éclairer les décisions et pérenniser les projets des personnes et des organisations que nous accompagnons.",
          },
          {
            num: "03",
            titre: "Notre vision",
            texte: "Une société où l'accès aux moyens nécessaires pour réussir ne dépend plus des ressources dont on dispose, mais du potentiel que l'on peut développer et de l'impact que l'on peut créer.",
          },
        ].map((c, i) => (
          <Reveal key={c.num} delay={i * 0.1}>
            <div className="grid gap-6 border-t border-border pt-10 md:grid-cols-12" data-testid={`manifesto-chapter-${c.num}`}>
              <span className="font-serif text-6xl font-medium text-gold md:col-span-3 md:text-7xl">{c.num}</span>
              <div className="md:col-span-6 md:col-start-5">
                <h3 className="font-serif text-2xl font-semibold md:text-3xl">{c.titre}</h3>
                <p className="mt-4 text-base leading-relaxed text-foreground/70">{c.texte}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Valeurs = () => (
  <section className="bg-espresso py-28 text-ivory" data-testid="valeurs-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Nos valeurs</p>
        <h2 className="mt-6 max-w-2xl font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl">
          Ce qui guide chacun de nos gestes
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-px overflow-hidden border border-espresso-border bg-espresso-border md:grid-cols-2 lg:grid-cols-4">
        {[
          { icone: Scale, titre: "Accessibilité", texte: "Des contenus en langage clair, des tarifs adaptés et des services gratuits pour les personnes qui en ont le plus besoin." },
          { icone: Landmark, titre: "Rigueur", texte: "Une information juridique et fiscale exacte, à jour et validée par des professionnels du droit et de la finance." },
          { icone: Compass, titre: "Empathie", texte: "Un accueil sans jugement, à l'écoute des réalités de chacun, parce que chaque parcours est unique." },
          { icone: ArrowUpRight, titre: "Autonomisation", texte: "Nous ne faisons pas à la place des gens : nous leur donnons les clés pour décider et agir par eux-mêmes." },
        ].map((v, i) => (
          <Reveal key={v.titre} delay={i * 0.1} className="bg-espresso">
            <div className="h-full p-10 transition-colors duration-300 hover:bg-espresso-2" data-testid={`valeur-${i}`}>
              <v.icone size={28} strokeWidth={1.5} className="text-gold" />
              <h3 className="mt-6 font-serif text-2xl font-semibold">{v.titre}</h3>
              <p className="mt-3 text-sm leading-relaxed text-ivory/60">{v.texte}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FormationsPreview = () => {
  const [formations, setFormations] = useState([]);
  useEffect(() => {
    getFormations().then((data) => setFormations(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <section className="bg-background py-28" data-testid="formations-preview-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Formations</p>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl">
              Apprendre, comprendre, agir
            </h2>
          </div>
          <Link
            to="/formations"
            data-testid="formations-preview-all-link"
            className="group flex items-center gap-2 text-sm font-semibold text-primary"
          >
            Toutes les formations
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </Reveal>

        <div className="mt-14">
          {formations.map((f, i) => (
            <Reveal key={f.id} delay={i * 0.08}>
              <Link
                to="/formations"
                className="group grid items-center gap-4 border-t border-border py-8 transition-colors duration-200 hover:bg-card md:grid-cols-12"
                data-testid={`formation-preview-${i}`}
              >
                <div className="md:col-span-6">
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{f.categorie}</span>
                  <h3 className="mt-2 font-serif text-2xl font-semibold leading-snug transition-colors group-hover:text-primary">
                    {f.titre}
                  </h3>
                </div>
                <p className="text-sm text-foreground/60 md:col-span-3">{f.format} · {f.duree}</p>
                <div className="flex items-center justify-between md:col-span-3 md:justify-end md:gap-6">
                  <span className={`rounded-sm px-3 py-1 text-xs font-semibold ${f.prix === 0 ? "bg-gold/20 text-[hsl(40,60%,32%)]" : "bg-muted text-foreground/70"}`} data-testid={`formation-preview-price-${i}`}>
                    {etiquettePrix(f)}
                  </span>
                  <ArrowUpRight size={20} className="text-foreground/40 transition-transform duration-200 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-primary" />
                </div>
              </Link>
            </Reveal>
          ))}
          <div className="border-t border-border" />
        </div>
      </div>
    </section>
  );
};

const ArticlesPreview = () => {
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    getArticles().then((data) => setArticles(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <section className="border-t border-border bg-card py-28" data-testid="articles-preview-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Le blogue</p>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl">
              Savoirs à partager
            </h2>
          </div>
          <Link
            to="/blogue"
            data-testid="blogue-preview-all-link"
            className="group flex items-center gap-2 text-sm font-semibold text-primary"
          >
            Tous les articles
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-12">
          {articles.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.1} className={i === 0 ? "lg:col-span-7" : "lg:col-span-5"}>
              <Link to={`/blogue/${a.slug}`} className="group block border-t-2 border-foreground/80 pt-6" data-testid={`article-preview-${i}`}>
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{a.categorie}</span>
                <h3 className={`mt-3 font-serif font-semibold leading-snug transition-colors group-hover:text-primary ${i === 0 ? "text-3xl md:text-4xl" : "text-2xl"}`}>
                  {a.titre}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground/60">{a.extrait}</p>
                <p className="mt-4 text-xs text-foreground/40">{a.date} · {a.lecture} de lecture</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CtaBanner = () => (
  <section className="relative overflow-hidden bg-espresso py-32 text-ivory" data-testid="cta-banner-section">
    <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-primary/15 blur-3xl" aria-hidden="true" />
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Soutenir la mission</p>
        <h2 className="mt-6 max-w-3xl font-serif text-4xl font-medium leading-[1.12] tracking-tight md:text-6xl">
          Chaque don rend le savoir <span className="italic text-gold">accessible</span> à une personne de plus.
        </h2>
        <div className="mt-12 flex flex-wrap gap-4">
          <Link
            to="/don"
            data-testid="cta-donate-button"
            className="rounded-sm bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(22,83%,40%)]"
          >
            Faire un don
          </Link>
          <Link
            to="/devenir-benevole"
            data-testid="cta-volunteer-button"
            className="rounded-sm border border-ivory/25 px-8 py-4 text-sm font-semibold text-ivory transition-colors duration-200 hover:border-gold hover:text-gold"
          >
            Devenir bénévole
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Partenaires = () => {
  const [partenaires, setPartenaires] = useState([]);
  useEffect(() => {
    getPartenairesPublic().then(setPartenaires).catch(() => {});
  }, []);
  if (partenaires.length === 0) return null;
  return (
    <section className="border-t border-border bg-card py-20 md:py-28" data-testid="partenaires-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Nos Partenaires</p>
          <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl" data-testid="partenaires-title">
            Ils s'associent à <span className="italic text-primary">notre mission</span>
          </h2>
        </Reveal>
        <div className="mt-14 flex flex-wrap items-stretch gap-6">
          {partenaires.map((p, i) => (
            <Reveal key={`${p.organisation}-${i}`} delay={i * 0.08}>
              <div className="flex h-full w-64 flex-col items-center border border-border bg-background p-8 text-center" data-testid={`partenaire-card-${i}`}>
                <img
                  src={`${BACKEND}/api/media/${p.logo_path}`}
                  alt={`Logo de ${p.organisation}`}
                  className="h-16 w-auto max-w-full object-contain"
                  data-testid={`partenaire-logo-${i}`}
                />
                <p className="mt-5 font-serif text-lg font-semibold" data-testid={`partenaire-nom-${i}`}>{p.organisation}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <Stats />
      <Marquee />
      <Manifesto />
      <Valeurs />
      <Temoignages />
      <FormationsPreview />
      <ArticlesPreview />
      <Partenaires />
      <CtaBanner />
    </>
  );
}
